import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { VideoTypesDataService, VideoTypesColumnsService } from '../../services';
import { DialogVideoTypeFormComponent } from './video-type-form/video-type-form.component';
import { DialogVideoTypeDetailsComponent } from './video-type-details/video-type-details.component';
import { VideoType, VideoTypesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-video-types',
  templateUrl: './video-types.component.html',
  styleUrls: ['./video-types.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class VideoTypesComponent implements OnInit, OnDestroy {

  private videoTypeAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private videoTypeEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private videoTypeDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public videoTypeToDelete: VideoType = {};
  public videoTypes: VideoType[] = [];
  public videoType: VideoType = {};
  public selectedVideoTypes: VideoType[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private videoTypesDataService: VideoTypesDataService,
    private videoTypesColumnsService: VideoTypesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('VideoTypesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Video Types - Management');
    this.primengConfig.ripple = true;
    this.getVideoTypes();
  }

  ngOnDestroy(): void {
    console.log('VideoTypesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getVideoTypes(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.videoTypesDataService.getVideoTypes(this.parameters).subscribe((response: VideoTypesResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.videoTypes = response.results;
        } else {
          this.videoTypes = this.videoTypes.concat(response.results);
        }
        if (response.inlineCount > this.videoTypes.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getVideoTypes();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOTYPESNOTLOADED') });
    });
  }

  importVideoTypes(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.videoTypesDataService.addVideoTypes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('VIDEOTYPESCREATED') });
                if (proceed === j) {
                  this.getVideoTypes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOTYPESNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOTYPESNOTCREATED') + ' ' + msg });
            });
          } else {
            this.videoTypesDataService.updateVideoTypes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('VIDEOTYPESUPDATED') });
                if (proceed === j) {
                  this.getVideoTypes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOTYPESNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('VIDEOTYPESNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.videoTypesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('videoTypes.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedVideoTypes() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Video Types?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.videoTypesDataService.deleteVideoTypes(this.selectedVideoTypes).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Video Types Deleted', life: 3000 });
            this.getVideoTypes();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Video Types not deleted' });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Video Types not deleted' });
        });
        this.selectedVideoTypes = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.videoTypes, this.columns, 'VideoTypes');
  }

  exportExcel() {
    ToolsService.exportExcel(this.videoTypes, this.columns, 'VideoTypes');
  }

  exportCsv() {
    ToolsService.exportCsv(this.videoTypes, this.columns, 'VideoTypes');
  }

  refresh() {
    this.videoTypesDataService.clearCache();
    this.getVideoTypes();
  }

  videoTypeAdd(): void {
    if (!this.navigation.getParamValue('videoTypes.addInDialog', true)) {
      this.router.navigate(['/video-types/add']);
      return;
    }
    this.videoTypeAddDialog = this.dialogService.open(DialogVideoTypeFormComponent, {
      header: '', // this.translate.instant('NEWVIDEOTYPE'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        videoType: { id: null },
        users: this.videoTypesColumnsService.users,
      },
    });

    this.videoTypeAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'VideoType added' });
        this.getVideoTypes();
      }
    });
  }

  videoTypeEdit(videoType: VideoType): void {
    if (!this.navigation.getParamValue('videoTypes.editInDialog', true)) {
      this.router.navigate(['/video-types/edit/' + videoType.id]);
      return;
    }
    this.videoTypeEditDialog = this.dialogService.open(DialogVideoTypeFormComponent, {
      header: '', // this.translate.instant('EDITVIDEOTYPE') + ' ' + videoType.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        videoType: videoType,
        users: this.videoTypesColumnsService.users,
      },
    });

    this.videoTypeEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'VideoType updated' });
        this.getVideoTypes();
      }
    });
  }

  videoTypeDetails(videoType: VideoType): void {
    if (!this.navigation.getParamValue('videoTypes.detailsInDialog', true)) {
      this.router.navigate(['/video-types/details/' + videoType.id]);
      return;
    }
    this.videoTypeDetailsDialog = this.dialogService.open(DialogVideoTypeDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        videoType: videoType,
      }
    });
  }

  videoTypeDelete(videoType: VideoType): void {
    this.videoTypeToDelete = videoType;
    this.messageService.clear();
    this.messageService.add({
      key: 'videoTypeDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + videoType.name + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.videoTypesDataService.deleteVideoType(this.videoTypeToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Video Type deleted' });
      this.getVideoTypes();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Video Type not deleted' });
    });
  }

  onDeleteReject(): void {
    this.videoTypeToDelete = {};
    this.messageService.clear('videoTypeDelete');
  }

}
